import { Check, ChevronDown, FileCheck, Grid3x3, Save } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import BackLink from '../BackLink'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'

function getStatusInfo(status) {
  switch (status) {
    case 'draft': return { label: 'Draft', variant: 'orange' }
    case 'open': return { label: 'Open for Picks', variant: 'teal' }
    case 'locked': return { label: 'Locked', variant: 'blue' }
    case 'complete': return { label: 'Complete', variant: 'teal' }
    default: return { label: status || 'Unknown', variant: 'muted' }
  }
}

export default function BracketStickyHeader({
  stickyHeaderRef,
  isHeaderSticky,
  tournament,
  bracket,
  bracketId,
  tournamentBrackets,
  saving,
  onSave,
}) {
  const navigate = useNavigate()
  const statusInfo = getStatusInfo(bracket?.status)

  return (
    <div
      ref={stickyHeaderRef}
      className={`sticky top-0 z-40 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 mb-6 backdrop-blur-md transition-all duration-200 ${
        isHeaderSticky
          ? 'bg-background/80 border-b border-border shadow-sm'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="flex items-center justify-between py-3 flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Auto-save when leaving the seed editor via the back breadcrumb.
              `silent: true` skips the validation-errors confirm prompt — the
              admin clicked "back" so we just preserve the work in progress.
              If the save itself fails (network, permission, etc.), onSave
              returns false and BackLink suppresses the navigation so the
              user sees the error toast and can retry. */}
          <BackLink
            label={tournament.name}
            to={`/admin/tournaments/${tournament.id}/edit`}
            onBeforeNavigate={() => onSave({ silent: true })}
          />
          <span className="text-sm text-muted-foreground">&rsaquo;</span>
          {tournamentBrackets.length > 1 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1 text-sm font-medium hover:text-primary transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm">
                  {bracket.divisionName}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {tournamentBrackets.map(b => (
                  <DropdownMenuItem
                    key={b.id}
                    onClick={() => {
                      if (b.id !== bracketId) {
                        navigate(`/bracket/${b.id}/seed`)
                      }
                    }}
                    className="flex items-center justify-between"
                  >
                    {b.divisionName}
                    {b.id === bracketId && (
                      <Check className="h-4 w-4 text-primary ml-4" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <span className="text-sm font-medium">{bracket.divisionName}</span>
          )}
          {bracket?.status && (
            <Badge variant={statusInfo.variant} className="py-1 px-3">
              {statusInfo.label}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="sm:h-10 sm:px-4 sm:text-sm"
            onClick={() => navigate(`/bracket/${bracketId}/picks?entryId=official&mode=editOfficial`)}
          >
            <FileCheck className="h-4 w-4 sm:mr-2" />
            <span className="ml-1.5 sm:ml-0 sm:hidden">Results</span>
            <span className="hidden sm:inline">Edit Official Results</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="sm:h-10 sm:px-4 sm:text-sm"
            onClick={() => navigate(`/bracket/${bracketId}/picks?mode=adminView`)}
          >
            <Grid3x3 className="h-4 w-4 sm:mr-2" />
            <span className="ml-1.5 sm:ml-0 sm:hidden">View</span>
            <span className="hidden sm:inline">View bracket</span>
          </Button>
          <Button variant="primaryNeon" size="sm" className="sm:h-10 sm:px-4 sm:text-sm" onClick={onSave} disabled={saving}>
            <Save className="h-4 w-4 mr-1.5 sm:mr-2" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  )
}
